import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import Button from '@amzn/meridian/button';
import Textarea from '@amzn/meridian/textarea';
import AgileManagementModals from '@components/agile/AgileManagementModals';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import { AgGridReact } from 'ag-grid-react';
import { useGetAsinMetadataQuery } from '@graphql/generated/graphql';
import useBatchSwapAssortmentAcrossZone from '@store/hooks/useBatchSwapAssortmentAcrossZone';
import usePromoteDraftZone from '@store/hooks/usePromoteDraftZone';
import useGetAssortmentDiff from '@store/hooks/useGetAssortmentDiff';
import { useAgileManagementEventHandlers } from '@components/agile/hooks/useAgileManagementEventHandlers';
import AgileManagementHeader from '@components/agile/components/AgileManagementHeader';
import { useAgileManagementTableData } from '@components/agile/hooks/useAgileManagementTableData';
import { getAgileTableColumnDefs } from '@components/agile/AgileTableColumnDefs';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { InputAsinsValidationUtils } from '@components/agile/utils/InputAsinsValidationUtils';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { AgileTableRow, ZoneGroup, RawItemData } from '@components/agile/types/AgileTableRow';
import 'src/components/shared/Agile/AgileBase.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import ColumnSelector from '@components/shared/ColumnSelector';
import { useColumnSelector } from 'src/hooks/useColumnSelector';
import FileInput, { FileDetails } from '@amzn/meridian/file-input';
import { useQuery } from '@tanstack/react-query';
import { MacroplanogramEntry, ZoneMapConfig } from 'src/graphql/generated/graphql';
import { FreshMacroPogDetailsAccessor, MacroPogDetailsAccessor } from 'src/accessors/MacroPogDetailsAccessor';
import {
  AssortmentModel,
  decorateAssortmentModelWithZoneMetadata,
  parseAssortmentSwapRecommendation,
} from './types/AssortmentModel';
import { CapacityUtilization, ItemCapacityAllocation, ZoneCapacity } from '../graphs/CapacityUtilization';

export interface AgileManagementProps {
  urlParams?: AgileURLParameterTypes;
  setUrlParameter?: (value: AgileURLParameterTypes) => void;
}

const AgileManagementV2 = (props: AgileManagementProps = {}) => {
  // move this to a MacroPog context program aware factory whenever implemented
  const macroPogDetailsAccessor: MacroPogDetailsAccessor = new FreshMacroPogDetailsAccessor();

  // A list of macroplanogram entries based on date and store
  const [workMacroPogList, setWorkMacroPogList] = useState<MacroplanogramEntry[]>([]);
  const [zoneMapConfigId, setZoneMapConfigId] = useState<string | undefined>();
  const [zoneMapConfig, setZoneMapConfig] = useState<ZoneMapConfig | undefined>();
  const [workAssortmentList, setWorkAssortmentList] = useState<AssortmentModel[]>([]);

  const { urlParams, setUrlParameter } = props;
  const [, setSearchParams] = useSearchParams();
  const [store, setStore] = useState(urlParams?.storeId);
  const [date, setDate] = useState(urlParams?.date || new Date().toISOString().split('T')[0]);
  const [rowData, setRowData] = useState<AgileTableRow[]>([]);
  const [inputAsins, setInputAsins] = useState('');
  const [csvAsins, setCsvAsins] = useState<string[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [pendingAsins, setPendingAsins] = useState<string[]>([]);
  const [isReset, setIsReset] = useState(false);
  const [diffProcessed, setDiffProcessed] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [deletedItems] = useState<Set<string>>(new Set());
  const lastIncludeDraft = useRef<boolean>();
  const [asinValidationError, setAsinValidationError] = useState<boolean>(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [swapErrorMsg, setSwapErrorMsg] = useState<string | undefined>(undefined);
  const [openPromoteDraftSuccessModal, setOpenPromoteDraftSuccessModal] = useState(false);
  const [includeDraft, setIncludeDraft] = useState(false);

  const { mutateAsync: batchSwapAssortmentAcrossZone } = useBatchSwapAssortmentAcrossZone();
  const { mutateAsync: promoteDraftZone } = usePromoteDraftZone();

  /* [API Queries] */
  const macroPogDataQuery = useQuery({
    queryKey: ['GetMacroPogData', store, date, includeDraft],
    queryFn: async () => {
      console.log(`Getting macroplanogram data for Store:${store}, date:${date}, draft:${includeDraft}`);

      // Load the macroplanogram entries
      const macroPogEntriesResponse = await macroPogDetailsAccessor.getMacroplanogramEntries({
        storeId: store!,
        timestamp: date,
        includeDraft,
        includeFuture: false,
      });

      const macroPogEntries: MacroplanogramEntry[] =
        macroPogEntriesResponse.macroplanogram?.filter((macro) => macro.isDraft === includeDraft) ?? [];

      // load assortment for macroPog entries
      const batchAssortmentRecomendation = await macroPogDetailsAccessor.getBatchAssortmentSwapRecommendations({
        batchAssortmentSwap: macroPogEntries.map((macro) => ({
          storeId: store!,
          includeDraft,
          macroplanogramId: macro.entryId,
          swapTime: date,
          addAssortment: [],
        })),
      });

      // join batch responses for assortment
      const assortmentRecommendations: AssortmentModel[] =
        batchAssortmentRecomendation.swapRecommendations
          .flatMap((response) => response.swapRecommendations || [])
          .map((entry) => parseAssortmentSwapRecommendation(entry))
          .map((entry) => decorateAssortmentModelWithZoneMetadata(entry, macroPogEntries)) ?? [];

      // fetch zone map configuration for zones
      let zoneMapConfig: ZoneMapConfig | undefined;
      let zoneMapConfigId: string | undefined;
      const zonesWithConfig = macroPogEntries.filter((me) => me.zoneMapConfigId);

      if (zonesWithConfig.length > 0 && zonesWithConfig[0].zoneMapConfigId) {
        zoneMapConfigId = zonesWithConfig[0].zoneMapConfigId;
        const getZoneMapConfigResponse = await macroPogDetailsAccessor.getZoneMapConfig({
          storeId: store!,
          productGroup: 'produce',
          zoneMapConfigName: store!,
          zoneMapConfigId: zoneMapConfigId,
        });
        zoneMapConfig = getZoneMapConfigResponse.zoneMapConfig ?? undefined;
      }

      return {
        zoneMapConfigId,
        zoneMapConfig,
        macroPogEntries,
        assortmentEntries: assortmentRecommendations,
      };
    },
    enabled: Boolean(store && date),
  });

  /* [API Queries] */
  // Everytime new macropog data is loaded set hook values
  useEffect(() => {
    setZoneMapConfigId(macroPogDataQuery.data?.zoneMapConfigId);
    setZoneMapConfig(macroPogDataQuery.data?.zoneMapConfig);
    setWorkMacroPogList(macroPogDataQuery.data?.macroPogEntries ?? []);
    setWorkAssortmentList(macroPogDataQuery.data?.assortmentEntries ?? []);
  }, [macroPogDataQuery.data]);

  // Transform zone map config to zone capacities format
  const zoneCapacities = useMemo(() => {
    if (!zoneMapConfig?.zones) return [];

    return zoneMapConfig.zones.map((zone) => ({
      zoneName: zone.name,
      totalFacings: zone.fixtures?.reduce((total, fixture) => total + (fixture.facings || 0), 0) || 0,
    }));
  }, [zoneMapConfig]);

  // Get metadata for new ASINs
  const { data: asinMetadataData } = useGetAsinMetadataQuery(
    {
      input: {
        asinList: pendingAsins,
        storeId: store || '',
      },
    },
    {
      enabled: pendingAsins.length > 0 && !!store,
    },
  );

  const asinMetadataList = asinMetadataData?.getAsinMetadata.asinMetadata;

  // Get assortment diff when reset is enabled and we have pending ASINs
  const assortmentDiffInput = useMemo(() => {
    if (!isReset || pendingAsins.length === 0 || !store) return null;

    return {
      storeId: store,
      timestamp: Date.now(),
      macroplanogramIds: workMacroPogList?.map((macro) => macro.entryId) || [],
      productEntries: pendingAsins.map((asin) => ({ asin })),
    };
  }, [isReset, pendingAsins, store, workMacroPogList]);

  const { result: assortmentDiffResult, isSuccess: isDiffSuccess } = useGetAssortmentDiff(
    assortmentDiffInput || { storeId: '', timestamp: 0, macroplanogramIds: [], productEntries: [] },
    workMacroPogList,
  );

  // Check if all ASINs have subcategories assigned and are not in unassigned zone
  const allAsinsHaveSubcategories = useMemo(() => {
    return rowData.every(
      (item) =>
        item.remainingFacings === 0 || // Skip items with 0 facings (deleted)
        (item.subcategoryId && item.subcategoryId.trim() !== '' && item.zoneName && item.zoneName !== 'Unassigned'),
    );
  }, [rowData]);

  // Check for FOH capacity validation errors (same as original AgileManagement)
  const hasFohCapacityErrors = useMemo(() => {
    const listOfCapacity = rowData.map((obj) => obj.fohCapacity.capacity);
    const listOfErrorMsg = rowData.map((obj) => obj.fohCapacity.errorMsg);
    const listOfSubcategoryId = rowData.map((obj) => obj.subcategoryId);

    return (
      listOfCapacity.includes(null) ||
      listOfCapacity.includes(0) ||
      listOfSubcategoryId.some((id) => id === undefined || id === '') ||
      listOfErrorMsg.some((error) => error !== null && error !== undefined)
    );
  }, [rowData]);

  // Check if there are any changes to review
  const hasChanges = useMemo(() => {
    const newAsins = rowData.filter((item) => item.isNewAsin);
    const modifiedAsins = rowData.filter((item) => !item.isNewAsin && item.difference !== 0);
    const deletedAsins = Array.from(deletedItems);
    return newAsins.length > 0 || modifiedAsins.length > 0 || deletedAsins.length > 0;
  }, [rowData, deletedItems]);

  // Combined validation for review button

  // Get changes for review modal
  const getChangesForReview = useCallback(() => {
    const newAsins = rowData.filter((item) => item.isNewAsin);
    const modifiedAsins = rowData.filter((item) => !item.isNewAsin && item.difference !== 0);

    // Get deleted items from deletedItems set
    const deletedAsins: AgileTableRow[] = Array.from(deletedItems)
      .map((itemKey) => {
        const [asin, zoneName] = itemKey.split('-');
        // Find the original item data from result.otherItems
        const originalItem = workAssortmentList.find(
          (item) =>
            item.asin === asin &&
            (item.macroplanogramName && item.macroplanogramName !== 'N/A' ? item.macroplanogramName : 'Unassigned') ===
              zoneName,
        );
        if (originalItem) {
          const macroplanogram = workMacroPogList?.find((mp) => mp.macroplanogramName === zoneName);
          return {
            asin,
            title: originalItem.title,
            image: originalItem.image || '',
            availableFacings: originalItem.facingUnits,
            remainingFacings: 0,
            difference: -originalItem.facingUnits,
            remainingCapacity: 0,
            remainingZoneFacings: 0,
            fohCapacity: {
              capacity: originalItem.fohCapacity.capacity || null,
              jumbleFactor: null,
              errorMsg: null,
            },
            uphl1: originalItem.uphl1 || '',
            uphl2: originalItem.uphl2 || '',
            uphl3: originalItem.uphl3 || '',
            buyable: originalItem.buyable || false,
            procurable: originalItem.procurable || false,
            temperatureRating: originalItem.temperatureRating || '',
            shrinkCost: originalItem.shrinkCost || 0,
            shrinkPerOps: originalItem.shrinkPerOps || 0,
            shrinkPerOps13week: originalItem.shrinkPerOps13week || 0,
            shrinkPerOps26week: originalItem.shrinkPerOps26week || 0,
            shrinkPerOps52week: originalItem.shrinkPerOps52week || 0,
            movingAvgSales13week: originalItem.movingAvgSales13week || 0,
            movingAvgSales26week: originalItem.movingAvgSales26week || 0,
            movingAvgSales52week: originalItem.movingAvgSales52week || 0,
            zoneName,
            zoneFacings: 0,
            macroplanogramId: macroplanogram?.entryId || '',
            entryId: originalItem.entryId || '',
            subcategoryId: originalItem.subcategoryId || '',
          } as AgileTableRow;
        }
        return null;
      })
      .filter((item): item is AgileTableRow => item !== null);

    return { newAsins, modifiedAsins, deletedAsins };
  }, [rowData, deletedItems, workAssortmentList, workMacroPogList]);

  const handleReviewChanges = useCallback(() => {
    setOpenConfirmationModal(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    const { newAsins, modifiedAsins, deletedAsins } = getChangesForReview();

    // Group by zone to handle multiple zones (include deleted items)
    const zoneGroups = [...newAsins, ...modifiedAsins, ...deletedAsins].reduce(
      (acc: Record<string, ZoneGroup>, item: AgileTableRow) => {
        const zoneId = item.macroplanogramId;
        if (zoneId && !acc[zoneId]) {
          return { ...acc, [zoneId]: { zoneName: item.zoneName, macroplanogramId: zoneId, items: [item] } };
        }
        if (zoneId) {
          return { ...acc, [zoneId]: { ...acc[zoneId], items: [...acc[zoneId].items, item] } };
        }
        return acc;
      },
      {} as Record<string, ZoneGroup>,
    );

    // Collect all swap inputs across all zones
    const allSwapInputs = Object.values(zoneGroups).map((zoneGroup: ZoneGroup) => {
      const zoneNewAsins = zoneGroup.items.filter((item: unknown) => (item as { isNewAsin?: boolean }).isNewAsin);
      const zoneModifiedAsins = zoneGroup.items.filter((item: unknown) => !(item as { isNewAsin?: boolean }).isNewAsin);

      const addAssortment = zoneNewAsins.map((item: unknown) => {
        const typedItem = item as {
          asin: string;
          remainingFacings: number;
          subcategoryId: string;
          remainingCapacity: number;
        };
        return {
          asin: typedItem.asin,
          facingUnits: typedItem.remainingFacings,
          subcategoryId: typedItem.subcategoryId,
          fohCapacity: typedItem.remainingCapacity,
        };
      });

      const assortmentFacingUpdates = zoneModifiedAsins.map((item: unknown) => {
        const typedItem = item as { entryId: string; remainingFacings: number; remainingCapacity: number };
        return {
          entryId: typedItem.entryId,
          facingUnits: typedItem.remainingFacings,
          fohCapacity: typedItem.remainingCapacity,
        };
      });

      return {
        storeId: store || '',
        macroplanogramId: zoneGroup.macroplanogramId,
        swapTime: date ? new Date(date).getTime() : Date.now(),
        endTime: workMacroPogList?.find((mp) => mp.entryId === zoneGroup.macroplanogramId)?.endTime,
        addAssortment,
        assortmentFacingUpdates,
        isDraft: includeDraft,
      };
    });

    try {
      const response = await batchSwapAssortmentAcrossZone({
        input: {
          swapAssortmentInputList: allSwapInputs,
        },
      });

      if (response.batchSwapAssortmentAcrossZone.errorMap.length > 0) {
        setSwapErrorMsg(response.batchSwapAssortmentAcrossZone.errorMap[0]?.error?.errorMessage || 'Unknown error');
        return;
      }
    } catch (error) {
      setSwapErrorMsg(error instanceof Error ? error.message : 'An unexpected error occurred');
      return;
    }

    // If we get here, all submissions were successful
    setOpenConfirmationModal(false);
    setSuccessful(true);
  }, [getChangesForReview, batchSwapAssortmentAcrossZone, store, date, workMacroPogList, includeDraft]);

  const handlePromoteDraft = useCallback(async () => {
    const { newAsins, modifiedAsins, deletedAsins } = getChangesForReview();

    // Group by zone to handle multiple zones (include deleted items)
    const zoneGroups = [...newAsins, ...modifiedAsins, ...deletedAsins].reduce(
      (acc: Record<string, ZoneGroup>, item: AgileTableRow) => {
        const zoneId = item.macroplanogramId;
        if (zoneId && !acc[zoneId]) {
          return { ...acc, [zoneId]: { zoneName: item.zoneName, macroplanogramId: zoneId, items: [item] } };
        }
        if (zoneId) {
          return { ...acc, [zoneId]: { ...acc[zoneId], items: [...acc[zoneId].items, item] } };
        }
        return acc;
      },
      {} as Record<string, ZoneGroup>,
    );

    // Collect all swap inputs across all zones
    const allSwapInputs = Object.values(zoneGroups).map((zoneGroup: ZoneGroup) => {
      const zoneNewAsins = zoneGroup.items.filter((item: unknown) => (item as { isNewAsin?: boolean }).isNewAsin);
      const zoneModifiedAsins = zoneGroup.items.filter((item: unknown) => !(item as { isNewAsin?: boolean }).isNewAsin);

      const addAssortment = zoneNewAsins.map((item: unknown) => {
        const typedItem = item as {
          asin: string;
          remainingFacings: number;
          subcategoryId: string;
          remainingCapacity: number;
        };
        return {
          asin: typedItem.asin,
          facingUnits: typedItem.remainingFacings,
          subcategoryId: typedItem.subcategoryId,
          fohCapacity: typedItem.remainingCapacity,
        };
      });

      const assortmentFacingUpdates = zoneModifiedAsins.map((item: unknown) => {
        const typedItem = item as { entryId: string; remainingFacings: number; remainingCapacity: number };
        return {
          entryId: typedItem.entryId,
          facingUnits: typedItem.remainingFacings,
          fohCapacity: typedItem.remainingCapacity,
        };
      });

      return {
        storeId: store || '',
        macroplanogramId: zoneGroup.macroplanogramId,
        swapTime: date ? new Date(date).getTime() : Date.now(),
        endTime: workMacroPogList?.find((mp) => mp.entryId === zoneGroup.macroplanogramId)?.endTime,
        addAssortment,
        assortmentFacingUpdates,
        isDraft: includeDraft,
      };
    });

    try {
      setOpenConfirmationModal(false); // Close confirmation modal first

      // First perform the batch swap
      const swapResult = await batchSwapAssortmentAcrossZone({
        input: {
          swapAssortmentInputList: allSwapInputs,
        },
      });

      if (swapResult.batchSwapAssortmentAcrossZone.errorMap.length > 0) {
        setSwapErrorMsg(swapResult.batchSwapAssortmentAcrossZone.errorMap[0]?.error?.errorMessage || 'Unknown error');
        return;
      }

      // Get minimum draft start time for promotion
      const getMinimumDraftStartTime = (macroplanograms: MacroplanogramEntry[]): number => {
        if (!macroplanograms || macroplanograms.length === 0) {
          return 0;
        }

        const draftMacroplanograms = macroplanograms.filter((macro) => macro.isDraft);
        if (draftMacroplanograms.length === 0) {
          return 0;
        }

        const startTimes = draftMacroplanograms.map((macro) => parseInt(macro.startTime, 10));
        return startTimes.length > 0 ? Math.min(...startTimes) : 0;
      };

      const minDraftStartTime = getMinimumDraftStartTime(workMacroPogList);

      // Then promote the draft
      const promoteResult = await promoteDraftZone({
        input: {
          storeId: store || '',
          timestamp: minDraftStartTime,
        },
      });

      if (promoteResult.promoteDraftZone.error === null) {
        setOpenPromoteDraftSuccessModal(true);
      } else {
        // TODO show error somehow promoteResult.promoteDraftZone.error?.errorMessage
        console.error(promoteResult.promoteDraftZone.error?.errorMessage ?? 'Error while promoting draft: Unknown');
      }
    } catch (error) {
      // TODO show error somehow error instanceof Error ? error.message : 'Unknown error occurred'
      console.error(
        error instanceof Error ? error.message : 'Error while promoting draft: Unknown' + JSON.stringify(error),
      );
    }
  }, [
    getChangesForReview,
    batchSwapAssortmentAcrossZone,
    promoteDraftZone,
    store,
    date,
    workMacroPogList,
    includeDraft,
  ]);

  const handleCloseModal = useCallback(() => {
    const wasSuccessful = successful || openPromoteDraftSuccessModal;
    setOpenConfirmationModal(false);
    setSuccessful(false);
    setSwapErrorMsg(undefined);
    // Reload page if the submission was successful
    if (wasSuccessful) {
      window.location.reload();
    }
  }, [successful, openPromoteDraftSuccessModal]);

  const isDataLoading = macroPogDataQuery.isLoading;

  // Filter out entries without macroplanogramName and transform data
  // Process table data - combine actionable and non-actionable items
  const { filteredData } = useAgileManagementTableData({
    otherItems:
      isReset && isDiffSuccess && assortmentDiffResult
        ? ([...assortmentDiffResult.otherItems, ...assortmentDiffResult.nonActionableItems] as RawItemData[])
        : ([...workAssortmentList] as RawItemData[]),
    zoneCapacities,
    deletedItems,
    asinMetadataList,
    macroplanogramList: workMacroPogList,
    isReset,
    pendingAsins,
  });

  // Reset rowData and pendingAsins when date, store, or includeDraft changes
  useEffect(() => {
    setRowData([]);
    setPendingAsins([]);
    setDiffProcessed(false); // Reset diff processed flag
  }, [date, store, includeDraft]);

  useEffect(() => {
    // Skip one render cycle when includeDraft changes to let filteredData recalculate
    if (lastIncludeDraft.current !== includeDraft) {
      lastIncludeDraft.current = includeDraft;
      return;
    }

    if (filteredData.length > 0 && rowData.length === 0) {
      // Set initial data when rowData is empty
      setRowData(filteredData);
    } else if (filteredData.length > 0) {
      // When reset is enabled and we have diff results, update once only
      if (isReset && isDiffSuccess && assortmentDiffResult && pendingAsins.length > 0 && !diffProcessed) {
        setRowData((prevData) => {
          // Create a set of pending ASINs for quick lookup
          const pendingAsinSet = new Set(pendingAsins);

          // Keep existing rows that are also in the pending ASINs list, mark them as MODIFY
          const preservedRows = prevData
            .filter((row) => pendingAsinSet.has(row.asin))
            .map((row) => ({
              ...row,
              recommendedAction: 'MODIFY' as const,
            }));

          // Add new rows from API that aren't already preserved
          const preservedAsins = new Set(preservedRows.map((row) => row.asin));
          const newRows = filteredData.filter((row) => !preservedAsins.has(row.asin));

          const allRows = [...preservedRows, ...newRows];

          // Sort by action: ADD first, then MODIFY, then REMOVE
          const actionOrder = { ADD: 1, MODIFY: 2, REMOVE: 3 };
          allRows.sort((a, b) => {
            const aOrder = actionOrder[a.recommendedAction as keyof typeof actionOrder] || 4;
            const bOrder = actionOrder[b.recommendedAction as keyof typeof actionOrder] || 4;
            return aOrder - bOrder;
          });

          return allRows;
        });
        setDiffProcessed(true);
        return;
      }

      // Reset the diffProcessed flag when reset is turned off
      if (!isReset && diffProcessed) {
        setDiffProcessed(false);
      }

      // Skip updates when in reset mode and already processed
      if (isReset && diffProcessed) {
        return;
      }

      setRowData((prevData) => {
        let updatedData = prevData;

        // When reset is on and we have pending ASINs, update REMOVE rows to 0 facings
        if (isReset && pendingAsins.length > 0) {
          updatedData = prevData.map((row) => {
            // Check if this row corresponds to a REMOVE action in the original API data
            const apiItem = [...workAssortmentList].find(
              (item) => item.asin === row.asin && item.macroplanogramName === row.zoneName,
            );
            if (apiItem?.action?.action === 'REMOVE') {
              return { ...row, remainingFacings: 0, difference: -row.availableFacings };
            }
            return row;
          });
        }

        // Add new ASINs
        const newAsins = filteredData.filter(
          (item) =>
            'isNewAsin' in item &&
            item.isNewAsin &&
            pendingAsins.includes(item.asin) &&
            !prevData.some((row) => row.asin === item.asin && row.zoneName === item.zoneName),
        );

        return newAsins.length > 0 ? [...newAsins, ...updatedData] : updatedData;
      });
    }
  }, [
    filteredData,
    pendingAsins,
    isReset,
    workAssortmentList,
    rowData.length,
    isDiffSuccess,
    assortmentDiffResult,
    diffProcessed,
    includeDraft,
  ]);

  // Reset the isReset flag when entering draft mode
  useEffect(() => {
    if (includeDraft) {
      setIsReset(false);
    }
  }, [includeDraft]);

  // Event handlers
  const { handleZoneChange, handleDeleteRow, onCellValueChanged, handleSubcategoryChange } =
    useAgileManagementEventHandlers({
      rowData,
      setRowData,
      zoneCapacities,
      macroplanogramList: workMacroPogList,
      setPendingAsins,
      productGroup: 'produce',
      store,
      zoneMapConfigId,
    });

  const customColDefs = getAgileTableColumnDefs({
    macroplanogramList: workMacroPogList || [],
    rowData,
    setRowData,
    onZoneChange: handleZoneChange,
    onDeleteRow: handleDeleteRow,
    includeDraft: includeDraft,
    onSubcategoryChange: handleSubcategoryChange,
  });

  // Column selection for Agile Management table
  const defaultVisibleColumns = useMemo(
    () => [
      'Delete', // Add delete column to default visible columns
      ...(isReset ? ['recommendedAction'] : []), // Show action column when reset is enabled
      'asin',
      'title',
      'image',
      'zoneName',
      'subcategoryId',
      'zoneFacings',
      'remainingZoneFacings',
      'remainingFacings',
      'difference',
      'fohCapacity',
      'buyable',
      'procurable',
      'temperatureRating',
      'shrinkPerOps13week',
      'shrinkPerOps26week',
      'movingAvgSales13week',
      'movingAvgSales52week',
    ],
    [isReset],
  );

  const {
    visibleColumns,
    columnDefs: baseFilteredColumnDefs,
    toggleColumn,
  } = useColumnSelector({
    allColumns: customColDefs,
    defaultVisibleColumns,
    storageKey: 'agileManagementTableColumns',
  });

  // Force show recommendedAction column when reset is enabled and we have diff results
  const filteredColumnDefs = useMemo(() => {
    if (isReset && isDiffSuccess && assortmentDiffResult && pendingAsins.length > 0) {
      return baseFilteredColumnDefs.map((col) => (col.field === 'recommendedAction' ? { ...col, hide: false } : col));
    }
    return baseFilteredColumnDefs;
  }, [baseFilteredColumnDefs, isReset, isDiffSuccess, assortmentDiffResult, pendingAsins.length]);

  // Real-time ASIN validation
  useEffect(() => {
    if (inputAsins.trim()) {
      const asinList = inputAsins
        .split(/[,\n]/)
        .map((asin) => asin.trim())
        .filter((asin) => asin);
      const isValid = InputAsinsValidationUtils(asinList);
      setAsinValidationError(!isValid);
    } else {
      setAsinValidationError(false);
    }
  }, [inputAsins]);

  const handleAddAsins = useCallback(() => {
    // Combine textarea ASINs and CSV ASINs
    const textAreaAsinList = inputAsins
      .split(/[,\n]/)
      .map((asin) => asin.trim())
      .filter((asin) => asin);

    const allAsins = [...textAreaAsinList, ...csvAsins];

    if (!InputAsinsValidationUtils(allAsins)) {
      setAsinValidationError(true);
      return;
    }

    setPendingAsins(allAsins);
    setInputAsins('');
    setCsvAsins([]);
    setAsinValidationError(false);
  }, [inputAsins, csvAsins]);

  // CSV parsing functions (extracted from AtAcrossZoneBulkUpload)
  const parseCsvRow = useCallback((row: string, asinColumnIndex: number): string => {
    const regex = /,(?=(?:[^"']*["'][^"']*["'])*[^"']*$)/g;
    const values = row.split(regex);
    return asinColumnIndex !== -1 ? values[asinColumnIndex].trim() : '';
  }, []);

  const parseCSVToEntries = useCallback(
    async (inputFile: File): Promise<string[]> => {
      const fileReader = new FileReader();
      return new Promise((resolve, reject) => {
        fileReader.onload = (event) => {
          if (event.target && event.target.result) {
            const fileContent = event.target.result as string;
            const rows = fileContent.trim().split('\n');
            const asinList: string[] = [];
            const headerRow = rows[0].split(',');
            const asinColumnIndex = headerRow.findIndex((header) => header.trim().toLowerCase() === 'asin');

            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              const asin = parseCsvRow(row, asinColumnIndex);
              if (asin) asinList.push(asin);
            }
            resolve(asinList);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        fileReader.onerror = () => reject(fileReader.error);
        fileReader.readAsText(inputFile);
      });
    },
    [parseCsvRow],
  );

  const handleFileUpload = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setCsvFile(file);

      try {
        const asinList = await parseCSVToEntries(file);

        if (asinList.length === 0) {
          setAsinValidationError(true);
          return;
        }

        // Store CSV ASINs separately, don't add to pendingAsins yet
        setCsvAsins(asinList);
        setAsinValidationError(false);
      } catch (error) {
        console.error('CSV upload failed:', error);
        setAsinValidationError(true);
      }
    },
    [parseCSVToEntries],
  );

  const handleRemoveFile = useCallback(() => {
    setCsvFile(null);
    setCsvAsins([]);
    setAsinValidationError(false);
  }, []);

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store || '',
          date: date || '',
        },
        urlParams?.demo,
      ),
    );
    setUrlParameter?.({
      storeId: store || '',
      date: date || '',
      zoneId: '',
      demo: urlParams?.demo,
    });
  }, [store, date, urlParams?.demo, setSearchParams, setUrlParameter]);

  /** Calculate Capacity Allocation */ // TODO move this to it's own Assortment Component
  const zonesCapacities: ZoneCapacity[] = workMacroPogList.map(
    (me) =>
      ({
        zoneId: me.entryId,
        zoneName: me.macroplanogramName,
        zoneCapacity: me.maxFacingUnits,
        zoneRation: (() => {
          if (!zoneMapConfig || !zoneMapConfig.zones || !me.macroplanogramName) {
            return 1 / 8;
          }
          const matchingZone = zoneMapConfig.zones.find(
            (zone) => zone.name === me.macroplanogramName?.toLocaleLowerCase(),
          );
          if (
            !matchingZone ||
            !matchingZone.fixtures ||
            matchingZone.fixtures.length === 0 ||
            !matchingZone.fixtures[0]?.facings
          ) {
            return 1 / 8;
          }
          return 1 / matchingZone.fixtures[0].facings;
        })(),
      }) as ZoneCapacity,
  );

  const itemCapacityAllocation: ItemCapacityAllocation[] = rowData
    .filter((assortment) => assortment.zoneName !== 'Unassigned' && assortment.zoneName !== '')
    .map(
      (assortment) =>
        ({
          zoneId: assortment.macroplanogramId,
          zoneName: assortment.zoneName,
          category: assortment.title,
          capacityAllocated: assortment.remainingFacings, // TODO why is it called remaining?
        }) as ItemCapacityAllocation,
    );

  const totalAllocatedCapacity = Number(
    itemCapacityAllocation.reduce((sum, item) => sum + Number(item.capacityAllocated), 0),
  );
  const totalZoneCapacity = Number(zonesCapacities.reduce((sum, zone) => sum + Number(zone.zoneCapacity), 0));
  const fullyAllocated = totalZoneCapacity === totalAllocatedCapacity;

  const canReviewChanges = useMemo(() => {
    return fullyAllocated && allAsinsHaveSubcategories && hasChanges && !hasFohCapacityErrors;
  }, [fullyAllocated, allAsinsHaveSubcategories, hasChanges, hasFohCapacityErrors]);

  const canPromoteBasedOnCapacity = useMemo(() => {
    return fullyAllocated && !canReviewChanges; // fully allocated and has not pending changes
  }, [fullyAllocated, canReviewChanges]);

  return (
    <Column width="100%" height="100%">
      {/* Show message if store is missing */}
      {!store ? (
        <>
          <AgileManagementHeader
            store={store || null}
            onStoreSelect={setStore}
            date={date}
            onDateChange={setDate}
            includeDraft={includeDraft}
            onIncludeDraftChange={setIncludeDraft}
          />

          <Column alignmentHorizontal="center" alignmentVertical="center" height="100%" heights={['10%', '10%', '0%']}>
            <Heading level={1} type="h600">
              Agile Management Dashboard
            </Heading>
            <Heading level={1} type="h200">
              Please select a store and date to get started
            </Heading>
          </Column>
        </>
      ) : (
        <>
          <AgileManagementHeader
            store={store || null}
            onStoreSelect={setStore}
            date={date}
            onDateChange={setDate}
            includeDraft={includeDraft}
            onIncludeDraftChange={setIncludeDraft}
          />

          {/* Draft Mode Alert */}
          {includeDraft && (
            <Row alignmentHorizontal="center" spacingInset="200">
              <Alert size="large" type="informational">
                You are working in a draft zone - all assortment additions and modifications will be saved as drafts for
                later review
              </Alert>
              <Button onClick={handlePromoteDraft} disabled={!canPromoteBasedOnCapacity}>
                Promote Draft to live
              </Button>
            </Row>
          )}
          {/* Department Capacity Utilization */}
          {!isDataLoading && workMacroPogList && workMacroPogList.length > 0 && (
            <Row spacing="400" alignmentHorizontal="center" spacingInset="300">
              <Box type="outline" spacingInset="300" width={'100%'}>
                <Text type="h500">Department Capacity Utilization</Text>

                {!zoneMapConfig ? (
                  <Box spacingInset={'300'}>
                    <Alert type="informational">Capacity bars are not available for legacy data.</Alert>
                  </Box>
                ) : (
                  workMacroPogList.length > 0 && (
                    <CapacityUtilization
                      department="Produce"
                      zonesCapacities={zonesCapacities}
                      itemCapacityAllocations={itemCapacityAllocation}
                    />
                  )
                )}
              </Box>
            </Row>
          )}

          {/* Loading state for capacity data */}
          {isDataLoading && (
            <Row spacing="400" alignmentHorizontal="center" spacingInset="300">
              <Box width={'100%'}>
                <Box type="outline" spacingInset="300">
                  <Box spacingInset={'300'}>
                    <Text type="h500">Department Capacity Utilization</Text>
                  </Box>
                  <Box spacingInset={'300'}>
                    <Row alignmentHorizontal="center" spacing="200">
                      <Loader />
                      <Text type="b200">Loading capacity data...</Text>
                    </Row>
                  </Box>
                </Box>
              </Box>
            </Row>
          )}

          {/* Add ASINs Section */}
          <Row alignmentHorizontal="start" spacingInset="200">
            <Row alignmentVertical="center" spacingInset="200">
              <Textarea
                value={inputAsins}
                onChange={setInputAsins}
                placeholder="Enter a list of ASINs that you would like to add to the table"
                resize="auto"
                rows={3}
                helperText="For multiple ASINs use commas (,) or new lines. eg: B00XXXXXFW, B00XXXXXF6 or one per line"
                error={asinValidationError}
                errorMessage={
                  asinValidationError
                    ? 'Invalid ASIN format. ASINs should be 10 characters: 9 digits + digit/X or 1 letter + 9 alphanumeric'
                    : undefined
                }
                width={400}
                disabled={csvFile !== null}
              />
              <Column alignmentVertical="center" spacingInset="100">
                <Text type="b200">Or upload CSV:</Text>
                <FileInput type="single" onFileAttached={handleFileUpload} accept="text/csv" />
                {csvFile && (
                  <FileDetails
                    file={csvFile}
                    onClickRemoveFile={handleRemoveFile}
                    removeFileButtonLabel={`Remove, ${csvFile.name}`}
                    uploadPercentage={100}
                    uploadComplete={true}
                    error={asinValidationError}
                    errorMessage={asinValidationError ? 'Invalid ASINs found in file' : undefined}
                  />
                )}
              </Column>
              <Column alignmentVertical="center" spacingInset="100">
                {!includeDraft && (
                  <Row alignmentVertical="center" spacingInset="100">
                    <Checkbox checked={isReset} onChange={setIsReset} />
                    <span>Reset?</span>
                  </Row>
                )}
                <Button
                  onClick={handleAddAsins}
                  disabled={(!inputAsins.trim() && csvAsins.length === 0) || asinValidationError}
                >
                  Add ASINs
                </Button>
              </Column>
            </Row>
          </Row>

          {/* Column Selector for Agile Management Table */}
          <Row alignmentHorizontal="end" spacingInset="200">
            <ColumnSelector allColumns={customColDefs} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
          </Row>

          <Row spacing="400" alignmentHorizontal="center" spacingInset="400">
            <div style={{ height: 'auto', width: 2000 }} className="ag-theme-alpine">
              {isDataLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Loader size="large" data-cy="agile_management_loading" />
                </div>
              ) : (
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={filteredColumnDefs}
                  onCellValueChanged={onCellValueChanged}
                  getRowId={(params) => params.data.rowId || `${params.data.asin}-${params.data.zoneName}`}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                  }}
                  rowHeight={50}
                  domLayout="autoHeight"
                  suppressColumnVirtualisation={true}
                  enableRangeSelection={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                />
              )}
            </div>
          </Row>

          {/* Review Changes Button - always visible, disabled when zones not fully utilized or missing subcategories */}
          <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
            <Button onClick={handleReviewChanges} disabled={!canReviewChanges}>
              Review Changes
            </Button>
          </Row>

          <AgileManagementModals
            openConfirmationModal={openConfirmationModal}
            successful={successful}
            swapErrorMsg={swapErrorMsg}
            onCloseModal={handleCloseModal}
            onConfirmSwap={handleSubmit}
            getChangesForReview={getChangesForReview}
            isDraft={includeDraft}
          />
        </>
      )}
    </Column>
  );
};

export default AgileManagementV2;
